import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PaymentService, {
  extractReceiptUrl,
} from "Services/Payment/PaymentService";
import useLang from "Shared/hooks/useLanguage";

const PaymentSuccess = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [receiptUrl, setReceiptUrl] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const paymentAttemptId = searchParams.get("paymentAttemptId")?.trim() ?? "";
  const sessionId =
    searchParams.get("sessionId")?.trim() ||
    searchParams.get("session_id")?.trim() ||
    "";

  useEffect(() => {
    let isMounted = true;

    const loadPaymentStatus = async () => {
      if (!paymentAttemptId || !sessionId) {
        if (isMounted) {
          setErrorMessage(
            t("Payment details are missing. Please return to Supply Orders.")
          );
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      setErrorMessage("");

      try {
        const res = await PaymentService.GetPaymentStatus(
          paymentAttemptId,
          sessionId
        );
        if (!isMounted) return;
        setReceiptUrl(extractReceiptUrl(res?.data));
      } catch (err: any) {
        if (!isMounted) return;
        setErrorMessage(
          err?.response?.data?.message ||
            t("Unable to load payment status. Please try again.")
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadPaymentStatus();

    return () => {
      isMounted = false;
    };
  }, [paymentAttemptId, sessionId, t]);

  return (
    <div>
      <div id="kt_app_toolbar" className="app-toolbar py-2 pt-lg-3">
        <div
          id="kt_app_toolbar_container"
          className="app-container container-fluid d-flex flex-wrap gap-4 justify-content-center justify-content-sm-between align-items-center"
        >
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <ul className="breadcrumb breadcrumb-separatorless fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <a href="" className="text-muted text-hover-primary">
                  {t("Home")}
                </a>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">
                {t("Supply Management")}
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">
                {t("Payment Success")}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="app-container container-fluid">
        <div className="card mb-5">
          <div className="card-body py-15">
            {isLoading ? (
              <div className="text-center">
                <div className="spinner-border text-primary mb-4" role="status">
                  <span className="visually-hidden">{t("Loading...")}</span>
                </div>
                <p className="text-muted mb-0">
                  {t("Confirming your payment, please wait...")}
                </p>
              </div>
            ) : errorMessage ? (
              <div className="text-center">
                <i
                  className="bi bi-exclamation-triangle text-danger mb-4 d-block"
                  style={{ fontSize: "3rem" }}
                  aria-hidden
                />
                <h3 className="fw-bold mb-3">{t("Payment Status Unavailable")}</h3>
                <p className="text-muted mb-6">{errorMessage}</p>
                <button
                  type="button"
                  className="btn btn-sm fw-bold btn-primary"
                  onClick={() => navigate("/manage-order")}
                >
                  {t("Go to Supply Orders")}
                </button>
              </div>
            ) : (
              <div className="text-center">
                <i
                  className="bi bi-check-circle-fill text-success mb-4 d-block"
                  style={{ fontSize: "3rem" }}
                  aria-hidden
                />
                <h3 className="fw-bold mb-3">{t("Payment Successful")}</h3>
                <p className="text-muted mb-6">
                  {t("Your payment has been completed successfully.")}
                </p>
                <div className="d-flex justify-content-center align-items-center gap-3 flex-wrap">
                  {receiptUrl ? (
                    <a
                      href={receiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm fw-bold btn-primary"
                    >
                      {t("View Payment Receipt")}
                    </a>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-sm fw-bold btn-secondary"
                    onClick={() => navigate("/manage-order")}
                  >
                    {t("Go to Supply Orders")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
